import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Box, CircularProgress } from '@mui/material';
import { useChat } from '../../chat/hooks/useChat';
import { useViewport } from '../../../shared/hooks/useViewport';
import InvestigationSidebar from '../components/InvestigationSidebar';
import InvestigationScopeModal from '../components/InvestigationScopeModal';
import InvestigationHeader from '../components/InvestigationHeader';
import InvestigationLayout from '../components/InvestigationLayout';
import InvestigationDataPanel, { type InvestigationDataTab } from '../components/InvestigationDataPanel';
import { useSidebarLayout } from '../hooks/useSidebarLayout';
import { useInvestigationModals } from '../components/useInvestigationModals';
import { useInvestigation } from '../context/InvestigationContext';
import { useInvestigationOperations } from '../hooks/useInvestigationOperations';
import { useInvestigationUrlSync } from '../hooks/useInvestigationUrlSync';
import KnowledgeViewModal from '../../knowledge/components/KnowledgeViewModal';
import FindingViewModal from '../../findings/components/FindingViewModal';
import AwsAccountManagementModal from '../../aws-account/components/AwsAccountManagementModal';
import ChatInterface from '../../chat/components/ChatInterface';
import { PromptPanel } from '../../prompt/components/PromptPanel';
import WorkflowRunModal from '../../workflow/components/WorkflowRunModal';
import WorkflowStartDialog from '../../workflow/components/WorkflowStartDialog';
import CaseControls from '../components/CaseControls';
import { useCasePlaybook } from '../hooks/useCasePlaybook';
import CreateInvestigationModal from '../components/CreateInvestigationModal';
import { useWorkflowRuns } from '../../workflow/hooks/useWorkflowRuns';
import { workflowSurfaces } from '../../workflow/utils/workflowSurfaces';
import { useWorkflowAccess } from '../../workflow/hooks/useWorkflowAccess';
import { PromptStudioModal } from '../../prompt-studio';
import { useFeatureSwitch } from '../../../shared/hooks/useFeatureSwitch';
import { EscalationProvider } from '../../customer/context/EscalationContext';
import { Investigation, InvestigationScope } from '../../../types';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { PHONE_TOAST_SX } from '../../../shared/constants/layout';

const InvestigationDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const investigationParam = searchParams.get('investigation');
  const { selectedCustomer, isCustomerError, refreshCustomer } = useInvestigation();

  const setInvestigationParam = useCallback((investigationId: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (investigationId) next.set('investigation', investigationId);
      else next.delete('investigation');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const { isCompact, isPhone } = useViewport();

  const sidebars = useSidebarLayout(isCompact);
  const [escalationState, setEscalationState] = useState({ effectiveLevel: 'l1', ceiling: 'none' });
  const [sidebarTab, setSidebarTab] = useState<InvestigationDataTab>('findings');
  // The panel draws the selected run; Expand puts that same drawing in a modal.
  const [runExpanded, setRunExpanded] = useState(false);
  // The workflow the operator picked, waiting on its access preflight. Starting straight from
  // the picker would skip that check and drop the access warnings a start comes back with.
  const [pendingWorkflow, setPendingWorkflow] = useState<string | null>(null);
  const [playbookOpen, setPlaybookOpen] = useState(false);

  const handleEffectiveLevelChange = useCallback((effectiveLevel: string, ceiling: string) => {
    setEscalationState({ effectiveLevel, ceiling });
  }, []);

  const escalationValue = useMemo(() => escalationState, [escalationState]);

  const {
    modalState,
    openModal,
    closeModal,
    viewKnowledgeItem,
    closeKnowledgeItem,
    viewFinding,
    closeFinding,
  } = useInvestigationModals();

  const {
    currentSession,
    setCurrentSession,
    sessions,
    setSessions,
    isLoading,
    error: chatError,
    isAgentTyping,
    currentStreamingMessage,
    agentActivity,
    connectionState,
    operationStartTime,
    sendMessage,
    cancelStream,
    clearConversation,
    createNewSession,
    selectSession,
    deleteSession,
    clearCurrentSession,
    editMessage,
    refreshInvestigation,
    contextUsage,
    messageQueue,
  } = useChat(selectedCustomer?.id, investigationParam);

  const workflowsEnabled = useFeatureSwitch('workflows');

  // The runs are read whatever the switch says: switching workflows off stops anything new, but a
  // run already under way stays readable and abandonable (FS-UC-02 3a). Off, the catalogue of
  // workflows to start is not read and the start, gate, branch and retry controls are absent.
  const workflowRuns = useWorkflowRuns({
    customerId: selectedCustomer?.id,
    investigationId: currentSession?.id,
    // A launcher start or a gate answer is an RPC write the agent never sees; this chat turn is
    // what makes it act. sendMessage queues while a reply streams and reports its own failures.
    tellAgent: sendMessage,
    startable: workflowsEnabled,
  });
  const workflowView = workflowSurfaces(workflowsEnabled, workflowRuns, setPendingWorkflow);
  const studioEnabled = useFeatureSwitch('prompt_studio');
  // The workflow an access failure sent the operator to edit; the Studio opens on its draft.
  const [editingWorkflowId, setEditingWorkflowId] = useState<string | null>(null);
  const workflowAccess = useWorkflowAccess({
    customerId: workflowsEnabled ? selectedCustomer?.id : undefined,
    investigationId: workflowsEnabled ? currentSession?.id : undefined,
  });

  const {
    knowledgeItems,
    findings,
    error: operationsError,
    handleCloseError,
    loadInvestigationData,
    handleReorderKnowledge,
    handleReorderFindings,
    handleExecutePlaybook,
    handleRemoveKnowledgeItem,
    handleRemoveFinding,
    handleAddAsFinding,
    handleToggleFindingRelevance,
    handleToggleFindingRootCause,
    handleDiveDeeper,
    handleMarkIrrelevant,
    handleUpdateFindingTags,
    handleManageScope,
    handleUpdateInvestigationMetadata,
    handleUpdateInvestigationStatus,
  } = useInvestigationOperations({
    currentSession,
    selectedCustomer,
    sessions,
    setSessions,
    setCurrentSession,
    refreshInvestigation,
    sendMessage,
  });

  // Was inside the chat box's own button: marking it executed is the record, and the message is
  // what actually makes the agent act. Kept together so neither can happen without the other.
  const handleRunPlaybook = useCallback(async () => {
    await handleExecutePlaybook();
    sendMessage('Execute the playbook');
  }, [handleExecutePlaybook, sendMessage]);

  // A chat turn is when the agent advances a run, and the runs query has no polling of its own.
  // Without this the panel sat stale behind the agent's own work until someone pressed Refresh,
  // which is the manual step that button existed to cover.
  const wasTyping = useRef(false);
  const refreshRuns = workflowRuns.refresh;
  useEffect(() => {
    if (wasTyping.current && !isAgentTyping) {
      void refreshRuns();
      void loadInvestigationData();
    }
    wasTyping.current = isAgentTyping;
  }, [isAgentTyping, refreshRuns, loadInvestigationData]);

  const casePlaybook = useCasePlaybook({
    customerId: selectedCustomer?.id,
    investigationId: currentSession?.id,
    onChanged: refreshInvestigation,
  });

  const displayError = chatError || operationsError || workflowRuns.agentNoticeError
    || casePlaybook.error;
  const { clearAgentNoticeError } = workflowRuns;
  const { clearError: clearPlaybookError } = casePlaybook;
  // Memoised to match handleCloseError, which the snackbar receives as a stable callback.
  const handleCloseDisplayError = useCallback(() => {
    handleCloseError();
    clearAgentNoticeError();
    clearPlaybookError();
  }, [handleCloseError, clearAgentNoticeError, clearPlaybookError]);

  // The provider loads the customer from the URL. If that id fails to load, go to
  // the customer picker in switch mode — bare '/' would let the landing page's
  // restore effect navigate straight back to the failing id and loop.
  useEffect(() => {
    if (isCustomerError) {
      navigate('/?switch=true');
    }
  }, [isCustomerError, navigate]);

  useEffect(() => {
    if (selectedCustomer) {
      if (currentSession && currentSession.customer_id !== selectedCustomer.id) {
        clearCurrentSession();
      }
    }
  }, [selectedCustomer, currentSession, clearCurrentSession]);

  useInvestigationUrlSync({
    investigationParam,
    sessionsLoaded: sessions.length > 0,
    currentSessionId: currentSession?.id,
    selectSession,
    setInvestigationParam,
  });

  const handleNewInvestigation = () => {
    if (!selectedCustomer) {
      return;
    }
    openModal('createInvestigation');
  };

  const handleInvestigationCreated = async (investigation: Investigation, initialMessage?: string) => {
    closeModal('createInvestigation');

    setSessions([investigation, ...sessions]);
    setCurrentSession({ ...investigation, messages: [] });

    if (selectedCustomer) {
      sessionStorage.setItem(`strands.tabInvestigationId.${selectedCustomer.id}`, investigation.id);
      localStorage.setItem(`strands.lastInvestigationId.${selectedCustomer.id}`, investigation.id);
    }
    setInvestigationParam(investigation.id);

    if (initialMessage) {
      setTimeout(() => {
        sendMessage(initialMessage);
      }, 100);
    }
  };

  const handleNewInvestigationScopeSelected = async (scope: InvestigationScope[]) => {
    closeModal('newInvestigationScope');
    if (!selectedCustomer) return;

    try {
      const newSession = await createNewSession(selectedCustomer);
      if (newSession) {
        setInvestigationParam(newSession.id);
        await handleManageScope(newSession.id, scope);
      }
    } catch {
      console.error('Failed to create investigation');
    }
  };

  if (!selectedCustomer) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const leftSidebarContent = (
    <InvestigationSidebar
      investigations={sessions}
      currentInvestigationId={currentSession?.id}
      onNewInvestigation={handleNewInvestigation}
      onSelectInvestigation={(id) => {
        setInvestigationParam(id);
        sidebars.closeSheet('left');
      }}
      onDeleteInvestigation={deleteSession}
      onManageScope={handleManageScope}
      onUpdateStatus={handleUpdateInvestigationStatus}
      onUpdateMetadata={handleUpdateInvestigationMetadata}
    />
  );

  const rightSidebarContent = (
    <InvestigationDataPanel
      tab={sidebarTab}
      onTabChange={setSidebarTab}
      findings={{
        findings,
        onViewFinding: viewFinding,
        onRemoveFinding: handleRemoveFinding,
        onReorderFindings: handleReorderFindings,
        onToggleRelevance: handleToggleFindingRelevance,
        onToggleRootCause: handleToggleFindingRootCause,
        onDiveDeeper: handleDiveDeeper,
        onMarkIrrelevant: handleMarkIrrelevant,
      }}
      knowledge={{
        knowledgeItems,
        onViewKnowledgeItem: viewKnowledgeItem,
        onRemoveKnowledgeItem: handleRemoveKnowledgeItem,
        onReorderKnowledge: handleReorderKnowledge,
      }}
      runs={workflowView.showRuns ? {
        runs: workflowRuns.runs,
        loading: workflowRuns.loading,
        error: workflowRuns.runsError,
        selectedRunId: workflowRuns.openRunId,
        onSelectRun: workflowRuns.openRun,
        detail: workflowRuns.detail ?? undefined,
        detailLoading: workflowRuns.detailLoading,
        onExpand: () => setRunExpanded(true),
        workflows: workflowRuns.runnable,
        ...workflowView.panel,
      } : null}
      onRefresh={() => {
        loadInvestigationData();
        workflowRuns.refresh();
      }}
    />
  );

  const mainContent = (
    <Box
      component="main"
      sx={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {currentSession?.playbook && (
        <PromptPanel
          playbook={currentSession.playbook}
          customerId={selectedCustomer.id}
          investigationId={currentSession.id}
          expanded={playbookOpen}
          onExpandedChange={setPlaybookOpen}
        />
      )}

      <ChatInterface
        currentSession={currentSession}
        customerId={selectedCustomer.id}
        currentStreamingMessage={currentStreamingMessage}
        isAgentTyping={isAgentTyping}
        agentActivity={agentActivity}
        isLoading={isLoading}
        operationStartTime={operationStartTime}
        contextUsage={contextUsage}
        messageQueue={messageQueue}
        sendMessage={sendMessage}
        cancelStream={cancelStream}
        clearConversation={clearConversation}
        editMessage={editMessage}
        onAddAsFinding={handleAddAsFinding}
        onNewInvestigation={handleNewInvestigation}
        onOpenInvestigations={() => sidebars.show('left')}
        procedure={
          // Not behind the workflow flag. A playbook is not a workflow, and gating these together
          // left an operator with workflows off unable to attach, read or run one at all.
          <CaseControls
            playbooks={casePlaybook.playbooks}
            onChoosePlaybook={casePlaybook.choose}
            playbookId={currentSession?.playbook?.id}
            playbookTitle={currentSession?.playbook?.title}
            playbookExecuted={currentSession?.playbook?.executed || false}
            onReadPlaybook={() => setPlaybookOpen(true)}
            onRunPlaybook={handleRunPlaybook}
            workflows={workflowRuns.runnable}
            runs={workflowRuns.runs}
            onRunWorkflow={setPendingWorkflow}
            onOpenRun={(runId) => {
              setSidebarTab('workflows');
              workflowRuns.openRun(runId);
            }}
            workflowsEnabled={workflowsEnabled}
          />
        }
      />
    </Box>
  );

  return (
    <EscalationProvider value={escalationValue}>
    <Box sx={{ display: 'flex', height: '100dvh', flexDirection: 'column' }}>
      <InvestigationHeader
        selectedCustomer={selectedCustomer}
        connectionState={connectionState}
        leftSidebarOpen={sidebars.open.left}
        rightSidebarOpen={sidebars.open.right}
        onToggleLeftSidebar={() => sidebars.toggle('left')}
        onToggleRightSidebar={() => sidebars.toggle('right')}
        onManageAccounts={() => openModal('accountManagement')}
        onCustomerUpdated={refreshCustomer}
        onInvestigationCreated={handleInvestigationCreated}
        onEffectiveLevelChange={handleEffectiveLevelChange}
        // While a sheet is open it is the only thing in reach: its Close button, the scrim and Escape close it.
        inert={isCompact && (sidebars.sheetOpen.left || sidebars.sheetOpen.right)}
      />

      <InvestigationLayout
        isCompact={isCompact}
        layout={sidebars}
        left={leftSidebarContent}
        main={mainContent}
        right={rightSidebarContent}
      />

      <SnackbarAlert
        snackbar={{ open: !!displayError, message: displayError || '', severity: 'error' }}
        onClose={handleCloseDisplayError}
        autoHideDuration={6000}
        // A top toast on a phone sits over the header's drawer toggles and customer switcher.
        anchorOrigin={{ vertical: isPhone ? 'bottom' : 'top', horizontal: 'center' }}
        sx={isPhone ? PHONE_TOAST_SX : undefined}
      />

      {workflowsEnabled && (
        <WorkflowStartDialog
          open={pendingWorkflow !== null}
          onClose={() => setPendingWorkflow(null)}
          workflow={workflowRuns.runnable.find((w) => w.name === pendingWorkflow) ?? null}
          catalogueError={workflowRuns.runnableError}
          onStart={workflowRuns.startWorkflow}
          access={workflowAccess}
          customerId={selectedCustomer?.id}
        />
      )}

      <CreateInvestigationModal
        isOpen={modalState.createInvestigation}
        customerId={selectedCustomer.id}
        onClose={() => closeModal('createInvestigation')}
        onInvestigationCreated={handleInvestigationCreated}
      />

      {/* Keyed on the run: the modal stays mounted, and every piece of per-gate state inside it
          (an open stop dialog, an unsent note, a gate error) belongs to one run. Node ids repeat
          across runs of the same workflow, so unkeyed state can be read as the next run's. */}
      <WorkflowRunModal
        key={workflowRuns.openRunId ?? 'none'}
        open={runExpanded && workflowRuns.openRunId !== null}
        detail={workflowRuns.detail}
        loading={workflowRuns.detailLoading}
        error={workflowRuns.detailError}
        onClose={() => setRunExpanded(false)}
        {...workflowView.modal}
        onEditWorkflow={studioEnabled ? setEditingWorkflowId : undefined}
      />

      {studioEnabled && (
        <PromptStudioModal
          customerId={selectedCustomer?.id}
          isOpen={editingWorkflowId !== null}
          onClose={() => setEditingWorkflowId(null)}
          editWorkflowId={editingWorkflowId ?? undefined}
        />
      )}

      <KnowledgeViewModal
        open={!!modalState.selectedKnowledgeItem}
        onClose={closeKnowledgeItem}
        item={modalState.selectedKnowledgeItem}
      />

      <FindingViewModal
        open={!!modalState.selectedFinding}
        onClose={closeFinding}
        finding={modalState.selectedFinding}
        onDiveDeeper={handleDiveDeeper}
        onUpdateTags={handleUpdateFindingTags}
      />

      {modalState.newInvestigationScope && (
        <InvestigationScopeModal
          isOpen={modalState.newInvestigationScope}
          customerId={selectedCustomer.id}
          title="Select Investigation Scope"
          onSave={handleNewInvestigationScopeSelected}
          onCancel={() => closeModal('newInvestigationScope')}
        />
      )}

      <AwsAccountManagementModal
        isOpen={modalState.accountManagement}
        customer={selectedCustomer}
        onClose={() => closeModal('accountManagement')}
      />

    </Box>
    </EscalationProvider>
  );
};

export default InvestigationDashboard;
